import { useEffect } from 'react'
import { RouterProvider, Outlet } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { I18nProvider } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { Toaster } from 'sonner'
import { router } from '@/router'
import { useAuthStore } from '@/stores/authStore'
import { useUIStore } from '@/stores/uiStore'
import { queryClient } from '@/lib/query'
import { UserTracker } from '@/components/common/UserTracker'
import { LegacyHashRedirectHandler } from '@/components/common'
import * as Sentry from "@sentry/react";

Sentry.init({
  dsn: "https://afc3fe1ec1e517f238147bc3d7d0458e@o4506200448040960.ingest.us.sentry.io/4510664031666176",
  environment: import.meta.env.VITE_NODE_ENV || 'development',
  // Only enable Sentry in production
  enabled: import.meta.env.VITE_NODE_ENV === 'production',
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true
});

// Root layout that wraps all routes - provides global functionality
export function RootLayout() {
  const checkAuth = useAuthStore((state) => state.checkAuth)
  const initTheme = useUIStore((state) => state.initTheme)
  const resolvedTheme = useUIStore((state) => state.resolvedTheme)

  // Initialize app on mount
  useEffect(() => {
    // Initialize theme (dark/light mode)
    initTheme()
    // Check authentication status
    checkAuth()
  }, [checkAuth, initTheme])

  const { i18n } = useTranslation()

  return (
    <I18nProvider locale={i18n.language}>
      <LegacyHashRedirectHandler />
      <UserTracker />
      <Outlet />
      {/* Toast notifications */}
      <Toaster
        position="top-right"
        expand={false}
        richColors
        theme={resolvedTheme === 'dark' ? 'dark' : 'light'}
        toastOptions={{
          style: {
            fontFamily: 'var(--font-body)',
          },
        }}
      />
    </I18nProvider>
  )
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      {/* React Query devtools - only in development */}
      {import.meta.env.DEV && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  )
}

export default App
